/// <amd-module name="Wit/BoardsHub/KanbanBoard/Components/KanbanBoardHeader" />
import * as React from "react";
import { TfsComponent } from "Tfs/Platform/TfsComponent";
import { IObservableArray, IObservableValue, ObservableArray, ObservableValue } from "VSS/Core/Observable";
import { getMenuItems } from "VSS/Features/PlatformUI/ContributedMenu";
import { IVssPageContext } from "VSS/Platform/Context";
import { IVssContributionService } from "VSS/Platform/Contribution";
import { onClickFPS } from "VSS/Platform/FPS";
import { IVssComponentContext, IVssLayoutManager } from "VSS/Platform/Layout";
import { routeUrl } from "VSS/Platform/Location";
import { Header, TitleSize } from "VSSUI/Header";
import { getFilterItem, getFilterItemIconProps, HeaderCommandBar, IHeaderCommandBarItem } from "VSSUI/HeaderCommandBar";
import { Link } from "VSSUI/Link";
import { IListBoxItem } from "VSSUI/ListBox";
import { IMenuItem, MenuItemType } from "VSSUI/Menu";
import { Observer, UncheckedObserver } from "VSSUI/Observer";
import { Tab, TabBar, TabSize } from "VSSUI/Tabs";
import { IBoardsDirectoryService } from "Wit/BoardsHub/Common/IBoardsDirectoryService";
import { BoardData } from "Wit/BoardsHub/Common/KanbanBoardContracts";
import { CardRuleTypes } from "Wit/BoardsHub/Common/StylesUtils";
import { IKanbanBoardService } from "Wit/BoardsHub/KanbanBoard/IKanbanBoardService";
import * as Resources from "Wit/BoardsHub/KanbanBoard/Resources";
import { getBacklogUrl } from "Wit/BoardsHub/KanbanBoard/Utils/RoutingUtils";
import { IBacklogLevelConfiguration, IBoardsHubContentHeaderData } from "Wit/Common/AgileControls/AgileContracts";
import { getBacklogLevelSelector } from "Wit/Common/AgileControls/BacklogLevelSelector";
import { WitTeachingBubble } from "Wit/Common/Controls/Boards/WitTeachingBubble";
import { FavoriteTypes } from "Wit/Common/Generated/Constants";
import { IKanbanBoardSettingsPanelProps } from "Wit/Common/KanbanBoardSettingsTypes/Components/KanbanBoardSettingsPanel.Props";
import { DirectoryItem } from "Wit/DirectoryService/Client/Contracts/Directory";
import { IDirectoryDropdownItem } from "Wit/DirectoryService/Models";
import { DirectoryDropdown } from "Wit/DirectoryView/Components/DirectoryDropdown";
import { DirectoryDropdownProvider } from "Wit/DirectoryView/DirectoryDropdownProvider";
import { isEmbedded, renderBoardSettingsPanel } from "./Util";

interface IKanbanBoardHeaderProps {
    boardId?: string;
    filterToggled: IObservableValue<boolean>;
    headerData: IBoardsHubContentHeaderData | undefined;
    onChangeBacklogLevel: (newLevel: string) => void;
    onSelectedTabChanged: (newTab: string) => void;
    onSelectTeam?: (event: React.SyntheticEvent<HTMLElement, Event>, item: IListBoxItem<IDirectoryDropdownItem>) => void;
    onSettingsSaved?: () => void;
    onBeforeSettingsSaved?: () => Promise<void>;
    selectedBacklogLevel?: string;
    selectedTab: IObservableValue<string>;
    teamSelection?: DirectoryItem;
    isFiltering: IObservableValue<boolean>;
    hasError: boolean;
}

export const BoardTabName = "board";
export const AnalyticsTabName = "analytics";
export const BoardsHeaderDataProvider = "ms.vss-work-web.kanban-board-content-header-data-provider";

export class KanbanBoardHeader extends TfsComponent<IKanbanBoardHeaderProps, {}> {
    private contributedFilterItemsObservable: IObservableArray<IMenuItem> = new ObservableArray([]);
    private contributedFilterItems: IMenuItem[] = [];
    private itemProvider: DirectoryDropdownProvider;
    private rightHeaderContent = new ObservableArray<IHeaderCommandBarItem>([]);
    private visibleBacklogLevels: ObservableArray<IBacklogLevelConfiguration> = new ObservableArray([]);
    private selectedBacklogLevel: ObservableValue<string> = new ObservableValue("");

    private fullScreenButton: IHeaderCommandBarItem = {
        ariaLabel: Resources.EnterFullScreen,
        iconProps: { iconName: "FullScreen" },
        id: "full-screen",
        important: true,
        onActivate: () => {
            const layoutManager = this.context.pageContext.getService<IVssLayoutManager>("IVssLayoutManager");
            layoutManager.toggleFullScreenMode();
            if (layoutManager.getFullScreenMode().value) {
                this.fullScreenButton.iconProps = { iconName: "BackToWindow" };
                this.fullScreenButton.tooltipProps = { text: Resources.ExitFullScreen };
                this.fullScreenButton.ariaLabel = Resources.ExitFullScreen;
            } else {
                this.fullScreenButton.iconProps = { iconName: "FullScreen" };
                this.fullScreenButton.tooltipProps = { text: Resources.EnterFullScreen };
                this.fullScreenButton.ariaLabel = Resources.EnterFullScreen;
            }
            this.rightHeaderContent.change(this.rightHeaderContent.value.indexOf(this.fullScreenButton), this.fullScreenButton);
        },
        subtle: true,
        tooltipProps: { text: Resources.EnterFullScreen }
    };

    private toggleFilterButton: IHeaderCommandBarItem = getFilterItem(
        () => (this.props.filterToggled.value = !this.props.filterToggled.value),
        this.props.isFiltering.value,
        /* important */ true,
        Resources.ToggleFilter
    );

    private backlogLevelSelector: IObservableValue<IHeaderCommandBarItem>;
    private disposeBacklogLevelSelector: () => void;
    private navigateToBacklog: IHeaderCommandBarItem;
    private settingsButton: IHeaderCommandBarItem;

    constructor(props: IKanbanBoardHeaderProps, context: IVssComponentContext) {
        super(props, context);

        const routeValues: { [key: string]: string } = {};
        if (this.projectData?.id) {
            routeValues["project"] = this.projectData.id;
        }

        const directoryHref = routeUrl(this.context.pageContext, "ms.vss-work-web.new-boards-directory-route", routeValues);
        const directoryService = context.pageContext.getService<IBoardsDirectoryService>("IBoardsDirectoryService");
        this.itemProvider = new DirectoryDropdownProvider({
            directoryService,
            artifactTypeName: Resources.BoardsArtifactName,
            iconProps: { iconName: "BacklogBoard", ariaHidden: true },
            directoryHref,
            favoriteType: FavoriteTypes.WORK_TEAMBOARD
        });

        const boardService = this.context.pageContext.getService<IKanbanBoardService>("IKanbanBoardService");
        const boardSettingsData = boardService.boardSettingsData;
        this.settingsButton = getBoardSettingsCommandBarButton(this.context.pageContext, {
            boardSettingsData,
            onSave: async () => {
                props.onSettingsSaved?.();
                return true;
            },
            onBeforeSettingsSaved: props.onBeforeSettingsSaved,
            teamId: props.teamSelection?.id ?? ""
        });

        const backlogHref =
            this.projectData && props.teamSelection && props.selectedBacklogLevel
                ? getBacklogUrl(this.context.pageContext, this.projectData.id, props.teamSelection.id, props.selectedBacklogLevel)
                : undefined;
        this.navigateToBacklog = {
            href: backlogHref,
            iconProps: { iconName: "NavigateForward" },
            id: "view-as-backlog",
            itemType: MenuItemType.Normal,
            onActivate: (item, event) => {
                // use an href so this menu item gets the link treatment to allow for ctrl + click,
                // while also using onClickFPS for quicker navigation within same tab
                if (this.navigateToBacklog.href) {
                    onClickFPS(this.context.pageContext, this.navigateToBacklog.href, true, event);
                }
            },
            rank: 2,
            subtle: true,
            text: Resources.ViewAsBacklog
        };

        const { levelSelector, dispose } = getBacklogLevelSelector({
            artifactName: Resources.BoardArtifactName,
            currentBacklogLevelName: this.selectedBacklogLevel,
            backlogLevels: this.visibleBacklogLevels,
            iconProps: { iconName: "BacklogBoard" },
            onBacklogLevelsUpdated: this.refreshHeaderContent
        });

        this.backlogLevelSelector = levelSelector;
        this.disposeBacklogLevelSelector = dispose;

        this.onSelectedTabChanged(props.selectedTab.value);
    }

    public componentDidMount() {
        this.updateBacklogSelector();

        const selectedBacklogLevelCallback = this.selectedBacklogLevel.subscribe(newLevel => {
            if (this.projectData && this.props.teamSelection) {
                this.navigateToBacklog.href = getBacklogUrl(this.context.pageContext, this.projectData.id, this.props.teamSelection.id, newLevel);
            }

            this.props.onChangeBacklogLevel(newLevel);
        });

        const backlogLevelSelectorCallback = this.backlogLevelSelector.subscribe(newLevelSelector => {
            // backlog level selector is the first item on the right side
            this.rightHeaderContent.change(0, newLevelSelector);
        });

        const prevDispose = this.disposeBacklogLevelSelector;
        this.disposeBacklogLevelSelector = () => {
            prevDispose();
            this.selectedBacklogLevel.unsubscribe(selectedBacklogLevelCallback);
            this.backlogLevelSelector.unsubscribe(backlogLevelSelectorCallback);
        };
    }

    public componentDidUpdate(prevProps: IKanbanBoardHeaderProps) {
        super.componentDidUpdate(this.props, this.state);
        this.getContributedFilterItems();
        this.refreshHeaderContent();
        this.updateBacklogSelector(prevProps);

        if (prevProps.teamSelection !== this.props.teamSelection && this.projectData && this.props.teamSelection && this.props.selectedBacklogLevel) {
            this.navigateToBacklog.href = getBacklogUrl(
                this.context.pageContext,
                this.projectData.id,
                this.props.teamSelection.id,
                this.props.selectedBacklogLevel
            );
        }
    }

    public componentWillUnmount() {
        super.componentWillUnmount();
        this.disposeBacklogLevelSelector();
    }

    public render(): JSX.Element | null {
        const { onSelectTeam, teamSelection, selectedBacklogLevel, selectedTab, hasError } = this.props;
        const embedded = isEmbedded();
        return (
            <Observer
                contributedFilterItems={{
                    observableValue: this.contributedFilterItemsObservable,
                    filter: () => {
                        this.contributedFilterItems = this.contributedFilterItemsObservable.value.map(item => {
                            return { ...item, ariaLabel: item.text, subtle: true, toolTipProps: { text: item.text }, text: undefined };
                        });
                        this.refreshHeaderContent();
                    }
                }}
                isFiltering={{
                    observableValue: this.props.isFiltering,
                    filter: (isFiltering: boolean) => {
                        this.onIsFilteringChanged(isFiltering);
                    }
                }}
            >
                {() => {
                    return (
                        <>
                            <Header
                                title={
                                    embedded && teamSelection ? (
                                        <>
                                            {teamSelection.name} {selectedBacklogLevel}
                                        </>
                                    ) : (
                                        <DirectoryDropdown itemProvider={this.itemProvider} onSelect={onSelectTeam} selectedItem={teamSelection} />
                                    )
                                }
                                titleSize={TitleSize.Large}
                            />
                            {hasError ? (
                                <></>
                            ) : (
                                <>
                                    <Header className="boards-tabs-header" commandBarItems={this.rightHeaderContent}>
                                        <TabBar
                                            className="flex-self-start"
                                            tabsClassName="boards-tabbar-tabs"
                                            selectedTabId={selectedTab}
                                            onSelectedTabChanged={(tabId: string) => this.onSelectedTabChanged(tabId)}
                                            renderAdditionalContent={
                                                embedded ? undefined : () => <HeaderCommandBar items={[this.navigateToBacklog]} />
                                            }
                                            tabSize={TabSize.Compact}
                                        >
                                            <Tab id={BoardTabName} name={Resources.BoardTabName} />
                                            <Tab id={AnalyticsTabName} name={Resources.AnalyticsTabName} />
                                        </TabBar>
                                    </Header>
                                    <UncheckedObserver rightHeaderContent={this.rightHeaderContent}>
                                        <WitTeachingBubble
                                            anchorElementClassName="board-settings-button"
                                            settingsEntryKey="SwimlaneRulesBubble"
                                            canShow={canShowSwimlaneRulesTeachingBubble(this.context.pageContext)}
                                            text={
                                                <>
                                                    <span>{Resources.SwimlaneRulesTeachingBubbleText}</span>
                                                    <Link href="http://aka.ms/swimlanerules" rel="noopener noreferrer" target="_blank">
                                                        {Resources.SwimlaneRulesTeachingBubbleLinkText}
                                                    </Link>
                                                </>
                                            }
                                            title={Resources.SwimlaneRulesTeachingBubbleTitle}
                                        />
                                    </UncheckedObserver>
                                </>
                            )}
                        </>
                    );
                }}
            </Observer>
        );
    }

    private updateBacklogSelector(prevProps?: IKanbanBoardHeaderProps): void {
        if (this.props.headerData && !this.props.headerData.exceptionInfo && (!prevProps || prevProps.headerData !== this.props.headerData)) {
            this.visibleBacklogLevels.value = this.props.headerData.visibleBacklogLevels;
        }

        // update when props signal a backlog change and doesn't match our current selection
        if (
            this.props.selectedBacklogLevel &&
            this.props.selectedBacklogLevel !== this.selectedBacklogLevel.value &&
            (!prevProps || prevProps.selectedBacklogLevel !== this.props.selectedBacklogLevel)
        ) {
            this.selectedBacklogLevel.value = this.props.selectedBacklogLevel;
        }
    }

    private onSelectedTabChanged(tabId: string): void {
        this.getContributedFilterItems();
        this.refreshHeaderContent(tabId);
        this.props.onSelectedTabChanged(tabId);
    }

    private refreshHeaderContent = (tabId?: string): void => {
        tabId = tabId ?? this.props.selectedTab.value;
        let commands: IHeaderCommandBarItem[];
        if (tabId === BoardTabName) {
            commands = [
                this.backlogLevelSelector.value,
                this.toggleFilterButton,
                ...this.contributedFilterItems,
                this.settingsButton,
                this.fullScreenButton
            ];
        } else {
            commands = [this.backlogLevelSelector.value, this.fullScreenButton];
        }
        if (isEmbedded()) {
            commands = commands.filter(c => c !== this.backlogLevelSelector.value && c !== this.fullScreenButton);
        }
        this.rightHeaderContent.value = commands;
    };

    private getContributedFilterItems = () => {
        const filterItemContext = {
            id: this.props.boardId,
            team: {
                id: this.teamData?.id,
                name: this.teamData?.name
            }
        };

        this.contributedFilterItemsObservable.value = getMenuItems(
            this.context.pageContext,
            "ms.vss-work-web.backlog-board-pivot-filter-menu",
            filterItemContext,
            undefined,
            true
        ).value;

        this.contributedFilterItems = this.contributedFilterItemsObservable.value.map(item => {
            return { ...item, ariaLabel: item.text, subtle: true, toolTipProps: { text: item.text }, text: undefined };
        });
    };

    private onIsFilteringChanged = (isFiltering: boolean): void => {
        const filterButtonIndex = this.rightHeaderContent.value.indexOf(this.toggleFilterButton);
        if (filterButtonIndex === -1) {
            return;
        }
        this.toggleFilterButton.iconProps = getFilterItemIconProps(isFiltering);
        this.rightHeaderContent.change(filterButtonIndex, this.toggleFilterButton);
    };
}

function getBoardSettingsCommandBarButton(
    pageContext: IVssPageContext,
    panelProps: Omit<IKanbanBoardSettingsPanelProps, "onDismiss">
): IHeaderCommandBarItem {
    const settingsButton: IHeaderCommandBarItem = {
        ariaLabel: Resources.ConfigureBoardSettings,
        iconProps: { iconName: "Settings" },
        id: "team-settings",
        important: true,
        onActivate: () => renderBoardSettingsPanel(pageContext, panelProps),
        subtle: true,
        tooltipProps: { text: Resources.ConfigureBoardSettings },
        className: "board-settings-button"
    };

    return settingsButton;
}

function canShowSwimlaneRulesTeachingBubble(context: IVssPageContext): boolean {
    const contributionService = context.getService<IVssContributionService>("IVssContributionService");
    const boardData = contributionService.getData<BoardData>("ms.vss-work-web.kanban-board-content-data-provider");

    if (!boardData) {
        return false;
    }

    const rows = boardData.boardModel.boardSettings.rows;
    // comparing to 1 since default row is always there
    if (rows.length <= 1) {
        return false;
    }

    const cardRules = boardData.boardModel.boardCardSettings.styles;
    return !cardRules.some(rule => rule.type === CardRuleTypes.SwimlaneRule);
}
